/**
 * Custom cart drawer.
 *
 * Replaces the theme's built-in drawer and the UpCart app drawer. All state comes
 * from /cart.js, so the drawer never drifts from the real cart.
 *
 * Opens when: the header cart icon is clicked, or anything dispatches
 * `ajaxProduct:added` (quick add on grid cards, the product page form).
 */
(function () {
  'use strict';

  /* The theme instantiates its own drawer on DOMContentLoaded. This file is
     deferred and runs after theme.js but before that happens, so replacing the
     constructor here stops the old drawer from ever being wired up. */
  if (window.theme && typeof theme.CartDrawer === 'function') {
    theme.CartDrawer = function CustomCartDrawerDisabled() {};
  }

  var root = null;
  var threshold = 8000;
  var isOpen = false;
  var busy = false;
  var lastRecSignature = '';
  var dragged = false;

  var routes = (window.theme && theme.routes) || {};
  /* Note: theme.routes.cart already ends in .js — do not append it again. */
  var CART_URL = routes.cart || '/cart.js';
  var ADD_URL = routes.cartAdd || '/cart/add.js';
  var CHANGE_URL = routes.cartChange || '/cart/change.js';

  /* ---------------------------------------------------------------- helpers */

  function $(sel, ctx) {
    return (ctx || root).querySelector(sel);
  }

  function money(cents) {
    var n = Number(cents || 0) / 100;
    var s = n % 1 === 0 ? n.toFixed(0) : n.toFixed(2);
    return '$' + s.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /* Ajax endpoints are inconsistent about price: line items use integer cents,
     while some product payloads use a decimal string. Normalise to cents. */
  function toCents(value) {
    if (typeof value === 'string') {
      return value.indexOf('.') > -1 ? Math.round(parseFloat(value) * 100) : parseInt(value, 10);
    }
    return Number(value || 0);
  }

  function sized(url, width) {
    if (!url) return '';
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  function esc(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function getJSON(url, options) {
    return fetch(url, options).then(function (r) { return r.json(); });
  }

  function postJSON(url, body) {
    return getJSON(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body)
    });
  }

  /* ------------------------------------------------------------ open / close */

  function open() {
    if (!root || isOpen) return;
    isOpen = true;
    root.classList.add('is-open');
    root.setAttribute('aria-hidden', 'false');
    document.body.classList.add('ccart-open');
    refresh();
  }

  function close() {
    if (!root || !isOpen) return;
    isOpen = false;
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('ccart-open');
  }

  /* ------------------------------------------------------------------ render */

  function renderShipping(total) {
    var text = $('[data-ccart-shipping]');
    var bar = $('[data-ccart-bar]');
    if (!text || !bar) return;

    var remaining = threshold - total;
    if (remaining > 0) {
      text.innerHTML = "You're <strong>" + money(remaining) + '</strong> away from <strong>free US shipping</strong>';
    } else {
      text.innerHTML = "You've unlocked <strong>free US shipping</strong>";
    }

    var pct = threshold > 0 ? Math.min(100, (total / threshold) * 100) : 100;
    bar.style.width = pct + '%';
  }

  function itemHTML(item, index) {
    var variant = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
    var line = index + 1;

    return '' +
      '<div class="ccart__item" data-line="' + line + '">' +
        '<img class="ccart__item-image" src="' + esc(sized(item.image, 200)) + '" alt="' + esc(item.product_title) + '" width="84" height="104" loading="lazy">' +
        '<div class="ccart__item-info">' +
          '<p class="ccart__item-title"><a href="' + esc(item.url) + '">' + esc(item.product_title) + '</a></p>' +
          (variant ? '<span class="ccart__item-variant">' + esc(variant) + '</span>' : '') +
          '<span class="ccart__item-price">' + money(item.final_line_price) + '</span>' +
          '<span class="ccart__qty">' +
            '<button type="button" class="ccart__qty-btn" data-ccart-qty="-1" aria-label="Decrease quantity">&minus;</button>' +
            '<span class="ccart__qty-value">' + item.quantity + '</span>' +
            '<button type="button" class="ccart__qty-btn" data-ccart-qty="1" aria-label="Increase quantity">+</button>' +
          '</span>' +
        '</div>' +
        '<button type="button" class="ccart__item-remove" data-ccart-remove aria-label="Remove item">' +
          '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 5l14 14M19 5L5 19" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>' +
        '</button>' +
      '</div>';
  }

  function render(cart) {
    if (!root) return;

    var count = cart.item_count || 0;

    var countEl = $('[data-ccart-count]');
    if (countEl) countEl.textContent = count < 10 ? '0' + count : String(count);

    var items = $('[data-ccart-items]');
    if (items) {
      items.innerHTML = (cart.items || []).map(itemHTML).join('');
    }

    var empty = $('[data-ccart-empty]');
    if (empty) empty.hidden = count > 0;

    var footer = $('[data-ccart-footer]');
    if (footer) footer.hidden = count === 0;

    var total = $('[data-ccart-total]');
    if (total) total.textContent = money(cart.total_price);

    renderShipping(cart.total_price || 0);
    syncHeaderCount(count);
    loadRecommendations(cart);
  }

  /* Keep the theme's header cart bubble in step with our changes. */
  function syncHeaderCount(count) {
    document.querySelectorAll('.cart-link__bubble').forEach(function (b) {
      b.classList.toggle('cart-link__bubble--visible', count > 0);
    });
    document.querySelectorAll('.cart-link__bubble-num').forEach(function (el) {
      el.textContent = count;
    });
  }

  /* --------------------------------------------------------- recommendations */

  function recHTML(product) {
    var image = product.featured_image || (product.images && product.images[0]) || '';
    var price = toCents(product.price != null ? product.price : (product.variants && product.variants[0] && product.variants[0].price));
    var variants = product.variants || [];
    var single = variants.length === 1;

    var sizes = variants.map(function (v) {
      return '<button type="button" class="ccart__rec-size" data-variant="' + v.id + '"' +
        (v.available ? '' : ' disabled') + '>' + esc(v.title) + '</button>';
    }).join('');

    return '' +
      '<div class="ccart__rec" data-rec>' +
        '<a href="' + esc(product.url) + '"><img class="ccart__rec-image" src="' + esc(sized(image, 300)) + '" alt="' + esc(product.title) + '" loading="lazy"></a>' +
        (single ? '' : '<div class="ccart__rec-sizes">' + sizes + '</div>') +
        '<div class="ccart__rec-foot">' +
          '<span class="ccart__rec-price">' + money(price) + '</span>' +
          '<button type="button" class="ccart__rec-add"' +
            (single && variants[0] ? ' data-variant="' + variants[0].id + '"' : '') +
            ' aria-label="Add ' + esc(product.title) + ' to cart">' +
            '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>' +
          '</button>' +
        '</div>' +
      '</div>';
  }

  function loadRecommendations(cart) {
    var section = $('[data-ccart-recs]');
    var track = $('[data-ccart-recs-track]');
    if (!section || !track) return;

    var first = (cart.items || [])[0];
    if (!first) {
      section.hidden = true;
      lastRecSignature = '';
      return;
    }

    /* Only refetch when the anchor product changes — avoids a request on every
       quantity tweak. */
    var signature = String(first.product_id);
    if (signature === lastRecSignature && track.children.length) {
      section.hidden = false;
      updateSliderThumb();
      return;
    }
    lastRecSignature = signature;

    var inCart = (cart.items || []).map(function (i) { return i.product_id; });

    getJSON('/recommendations/products.json?product_id=' + first.product_id + '&limit=10&intent=related')
      .then(function (data) {
        var products = (data.products || []).filter(function (p) {
          return inCart.indexOf(p.id) === -1;
        }).slice(0, 6);

        if (!products.length) {
          section.hidden = true;
          return;
        }
        track.innerHTML = products.map(recHTML).join('');
        section.hidden = false;
        track.scrollLeft = 0;
        updateSliderThumb();
      })
      .catch(function () {
        section.hidden = true;
      });
  }

  /* ------------------------------------------------------------------ actions */

  function refresh() {
    return getJSON(CART_URL).then(render).catch(function (err) {
      /* Surfaced rather than swallowed — a silent failure here just looks like
         an empty cart, which is very hard to diagnose. */
      console.warn('[cart] could not load ' + CART_URL, err);
    });
  }

  function changeLine(line, quantity, itemEl) {
    if (busy) return;
    busy = true;
    if (itemEl) itemEl.classList.add('is-busy');

    postJSON(CHANGE_URL, { line: line, quantity: quantity })
      .then(function (cart) {
        render(cart);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
      })
      .catch(function () {})
      .then(function () { busy = false; });
  }

  function addVariant(id) {
    if (busy) return;
    busy = true;

    postJSON(ADD_URL, { items: [{ id: Number(id), quantity: 1 }] })
      .then(function () { return refresh(); })
      .catch(function () {})
      .then(function () { busy = false; });
  }

  /* ------------------------------------------------------------------- events */

  /**
   * The recommendations row scrolls horizontally with a hidden scrollbar, so add
   * click-drag to move it on desktop. Vertical wheel is intentionally left alone
   * so it scrolls the page. Touch devices scroll natively.
   */
  /* Size and position the dark thumb to reflect how far the row is scrolled. */
  function updateSliderThumb() {
    var track = $('[data-ccart-recs-track]');
    var thumb = $('[data-ccart-recs-thumb]');
    if (!track || !thumb) return;

    var max = track.scrollWidth - track.clientWidth;
    if (max <= 1) {
      thumb.style.width = '100%';
      thumb.style.left = '0';
      return;
    }

    var widthPct = Math.max((track.clientWidth / track.scrollWidth) * 100, 10);
    thumb.style.width = widthPct + '%';
    thumb.style.left = ((track.scrollLeft / max) * (100 - widthPct)) + '%';
  }

  function initSliderDrag() {
    var track = $('[data-ccart-recs-track]');
    if (!track) return;

    track.addEventListener('scroll', updateSliderThumb, { passive: true });
    window.addEventListener('resize', updateSliderThumb);

    var down = false;
    var startX = 0;
    var startScroll = 0;

    track.addEventListener('pointerdown', function (evt) {
      if (evt.pointerType === 'touch') return;
      down = true;
      dragged = false;
      startX = evt.clientX;
      startScroll = track.scrollLeft;
    });

    track.addEventListener('pointermove', function (evt) {
      if (!down) return;
      var dx = evt.clientX - startX;
      if (Math.abs(dx) > 3) dragged = true;
      if (dragged) {
        track.scrollLeft = startScroll - dx;
        evt.preventDefault();
      }
    });

    ['pointerup', 'pointerleave', 'pointercancel'].forEach(function (name) {
      track.addEventListener(name, function () { down = false; });
    });

    /* Deliberately no wheel handler: converting vertical wheel into horizontal
       scroll traps the page/drawer when someone scrolls past this row.
       Horizontal wheel/trackpad gestures already scroll the track natively. */
  }

  function onClick(evt) {
    var t = evt.target;
    if (!t || typeof t.closest !== 'function') return;

    /* A drag ends in a click — don't let it add to cart or follow a link.
       Checked here because this listener is on document in capture phase, so it
       runs before anything bound to the track itself. */
    if (dragged) {
      dragged = false;
      if (t.closest('.ccart__recs-track')) {
        evt.preventDefault();
        evt.stopPropagation();
        return;
      }
    }

    /* Header cart icon — take over from the theme's drawer. */
    var opener = t.closest('.js-drawer-open-cart');
    if (opener && root) {
      evt.preventDefault();
      evt.stopPropagation();
      open();
      return;
    }

    if (!root || !root.contains(t)) return;

    if (t.closest('[data-ccart-close]')) {
      evt.preventDefault();
      close();
      return;
    }

    var qtyBtn = t.closest('[data-ccart-qty]');
    if (qtyBtn) {
      var itemEl = qtyBtn.closest('.ccart__item');
      var valueEl = itemEl && itemEl.querySelector('.ccart__qty-value');
      if (!itemEl || !valueEl) return;
      var next = parseInt(valueEl.textContent, 10) + parseInt(qtyBtn.getAttribute('data-ccart-qty'), 10);
      if (next < 0) next = 0;
      changeLine(parseInt(itemEl.getAttribute('data-line'), 10), next, itemEl);
      return;
    }

    var removeBtn = t.closest('[data-ccart-remove]');
    if (removeBtn) {
      var row = removeBtn.closest('.ccart__item');
      if (row) changeLine(parseInt(row.getAttribute('data-line'), 10), 0, row);
      return;
    }

    /* Recommendation "+": add directly when there's one variant, otherwise
       reveal the inline size picker. */
    var addBtn = t.closest('.ccart__rec-add');
    if (addBtn) {
      var direct = addBtn.getAttribute('data-variant');
      if (direct) {
        addVariant(direct);
      } else {
        var card = addBtn.closest('[data-rec]');
        if (card) {
          var wasOpen = card.classList.contains('is-picking');
          root.querySelectorAll('[data-rec].is-picking').forEach(function (c) { c.classList.remove('is-picking'); });
          if (!wasOpen) card.classList.add('is-picking');
        }
      }
      return;
    }

    var sizeBtn = t.closest('.ccart__rec-size');
    if (sizeBtn && !sizeBtn.disabled) {
      addVariant(sizeBtn.getAttribute('data-variant'));
      return;
    }

    /* Click elsewhere in the drawer closes any open size picker. */
    if (!t.closest('.ccart__rec-sizes')) {
      root.querySelectorAll('[data-rec].is-picking').forEach(function (c) { c.classList.remove('is-picking'); });
    }
  }

  function init() {
    root = document.getElementById('CustomCart');
    if (!root) return;

    var t = parseInt(root.getAttribute('data-threshold'), 10);
    if (!isNaN(t)) threshold = t;

    document.addEventListener('click', onClick, true);
    initSliderDrag();

    document.addEventListener('keydown', function (evt) {
      if (evt.key === 'Escape' || evt.key === 'Esc') close();
    });

    /* Any successful add anywhere on the site opens the drawer. */
    document.addEventListener('ajaxProduct:added', function () {
      open();
      refresh();
    });

    /* Dev-friendly hooks, mirroring the theme's own events. */
    document.addEventListener('cart:open', open);
    document.addEventListener('cart:close', close);

    refresh();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
